import type { Project } from "@/lib/types";

/**
 * Lazy-load info pattern. Instead of chasing the developer for every
 * piece of data up front (and burning Angel's bandwidth on speculative
 * needs), we surface "pendiente" categories on the project page and
 * let interested buyers REQUEST the specific info they care about.
 *
 * Trade-off accepted: at scale this becomes manual labor per inquiry.
 * That's fine for now — it's also higher-touch and converts better at
 * low volume. When inquiries pass ~5/week per project, swap to
 * proactive data collection (ping the developer once, populate the
 * row).
 */

export type MissingInfoKey =
  | "precios"
  | "plan_pago"
  | "entrega"
  | "renders";

export interface MissingInfoItem {
  key: MissingInfoKey;
  title: string;
  description: string;
}

const CATALOG: Record<MissingInfoKey, Omit<MissingInfoItem, "key">> = {
  precios: {
    title: "Precios actualizados",
    description:
      "Lista de precios por unidad o modelo del desarrollador, con la disponibilidad actual.",
  },
  plan_pago: {
    title: "Plan de pago y financiamiento",
    description:
      "Monto de reserva, % inicial al firmar, cuotas durante construcción, saldo a entrega, y bancos dominicanos que financian a no-residentes.",
  },
  entrega: {
    title: "Fecha estimada de entrega",
    description:
      "Cronograma actualizado del desarrollador, etapas en construcción y plazo previsto de entrega de unidades.",
  },
  renders: {
    title: "Renders y fotos adicionales",
    description:
      "Material visual extendido más allá del que aparece público — renders por modelo, fotos de avance de obra, planos, recorridos.",
  },
};

export function computeMissingInfo(project: Project): MissingInfoItem[] {
  const items: MissingInfoItem[] = [];
  if (project.priceFromUsd == null) {
    items.push({ key: "precios", ...CATALOG.precios });
  }
  if (project.planInitialPct == null) {
    items.push({ key: "plan_pago", ...CATALOG.plan_pago });
  }
  if (project.deliveryTargetDate == null) {
    items.push({ key: "entrega", ...CATALOG.entrega });
  }
  // Renders is always offerable — even projects with all numerical
  // data tend to be light on visual material, and "ver más" is a
  // soft conversion hook.
  items.push({ key: "renders", ...CATALOG.renders });
  return items;
}

/**
 * Whether to render the missing-info section at all. Renders alone
 * isn't enough — would noise up complete projects. Show the section
 * only when meaningful data is pending.
 */
export function shouldShowMissingInfo(project: Project): boolean {
  return (
    project.priceFromUsd == null ||
    project.planInitialPct == null ||
    project.deliveryTargetDate == null
  );
}

/**
 * Pre-fill text dropped into the inquiry form when the buyer taps
 * "Pídelo" on a specific item. They can still edit it before sending.
 */
export function buildPrefillMessage(
  projectTitle: string,
  itemKey: MissingInfoKey
): string {
  const item = CATALOG[itemKey];
  return `Me interesa recibir "${item.title}" de ${projectTitle}. ¿Pueden enviarme los detalles actualizados con el desarrollador?`;
}
